import { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { useHeaderContext } from "../../../../context/headerContext";
import { useUserContext } from "../../../../context/userContext";
import BandCard from '../../../../components/BandCard';
import { format, differenceInMinutes, differenceInHours, differenceInDays, differenceInWeeks } from 'date-fns';
import {APIProvider, Map} from '@vis.gl/react-google-maps';
import fetchWithRefresh from '../../../../utils/fetchWithRefresh';
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../../utils/LogErrors";

export default function IncidentHistorySingle() {
    const { setTitle, setBackButton } = useHeaderContext();
    const { state } = useLocation();
    const { user, logout, updateTokens } = useUserContext();
    const [participantBand, setParticipantBand] = useState(null);
    const [incidentNotes, setIncidentNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const incident = state?.userData || state?.participant;
    console.log(state);

    useEffect(() => {
      setTitle('INCIDENT DETAILS');
      setBackButton(true);
    }, []);

    useEffect(() => {
        fetchUserBand();
        fetchIncidentNotes();
        setLoading(false);
    }, [incident]);
 
    const fetchUserBand = async () => {
      await Sentry.startSpan({ name: "Fetch User Band", op: "http.request" }, async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Bands/FetchBand?bandid=${incident?.BandID}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );
    
          span.setAttribute("http.status_code", response.status);
    
          if (response.ok) {
            const data = await response.json();
            setParticipantBand(data);
          } else {
            throw new Error('Failed to fetch user band');
          }
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error fetching user band: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };

    const fetchIncidentNotes = async () => {
      await Sentry.startSpan({ name: "Fetch Incident Notes", op: "http.request" }, async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Incident/FetchIncidentNotes?IncidentId=${incident?.EmergencyId}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user?.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );
    
          span.setAttribute("http.status_code", response.status);
    
          if (response.ok) {
            const data = await response.json();
            setIncidentNotes(data);
          } else {
            throw new Error('Failed to fetch incident notes');
          }
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error fetching incident notes: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };    

    const whenOpened = incident?.WhenOpened ? new Date(incident.WhenOpened) : null;
    const whenClosed = incident?.WhenClosed ? new Date(incident.WhenClosed) : null;
    
    let duration = '';
    if (whenOpened && whenClosed) {
      const minutes = differenceInMinutes(whenClosed, whenOpened);
      const hours = differenceInHours(whenClosed, whenOpened);
      const days = differenceInDays(whenClosed, whenOpened);
      const weeks = differenceInWeeks(whenClosed, whenOpened);
    
      if (weeks > 0) {
      duration = `${weeks}w ${days % 7}d`;
      } else if (days > 0) {
      duration = `${days}d ${hours % 24}h`;
      } else if (hours > 0) {
      duration = `${hours}h ${minutes % 60}m`;
      } else {
      duration = `${minutes}m`;
      }
    } else {
      duration = 'N/A';
    }
  
    return (
      <div className='mt-[20px]'>
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <div className="loading loading-spinner loading-lg">Loading...</div>
        </div>
      ) : (
        <>
          <div className='p-[24px] pb-0'> 
            <BandCard size="sm" band={participantBand} participant={incident} type='participant' />
            <div className='mt-[60px] mb-[30px] pb-[30px] border-b border-dashed border-[#E3E3E3]'>
              <div>
                <p className='font-poppins font-semibold text-[24px]'>{incident?.ParticipantName}</p>
                <p className='font-poppins text-[#727272]'>{incident?.WhenOpened ? `${format(new Date(incident.WhenOpened), 'dd/MM/yyyy')} • ${format(new Date(incident.WhenOpened), 'HH:mm')} • ${duration}` : 'N/A'}</p>
              </div>
              <div className='flex flex-row gap-x-4 justify-between my-[30px]'>
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Venue</p>
                  <p className='font-poppins text-[#727272] capitalize'>{incident?.OrgPartnerFriendlyName}</p>
                </div>
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Zone</p>
                  <p className='font-poppins text-[#727272] capitalize'>{incident?.ZoneName}</p>
                </div>
              </div>
              {(incident?.Lat !== "null" && incident?.Lng !== "null") && (
                <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                  <Map
                    style={{width: '100%', height: '174px', borderRadius: '4px', border: '1px solid #E3E3E3'}}
                    defaultCenter={{
                      lat: parseFloat(incident?.Lat), 
                      lng: parseFloat(incident?.Lng)
                    }}
                    defaultZoom={16}
                    gestureHandling={'greedy'}
                    disableDefaultUI={true}
                  />
                </APIProvider>
              )}
            </div>
          </div>
          <div className='flex flex-col gap-y-[10px] bg-sportsLightGrey p-[24px]'>
            <div className='flex flex-col gap-y-[20px] border-b border-dashed mb-[10px] pb-[30px]'>
              <div>
                <p className="font-poppins font-semibold text-[16px] mb-[10px]">Incident Notes</p>
                {Array.isArray(incidentNotes) && incidentNotes.length > 0 ? (
                  <ul className='flex flex-col gap-y-[10px]'>
                    {incidentNotes.map((note, index) => (
                      <li className='font-poppins text-[#727272]' key={index}>{note.Note}</li>
                    ))}
                  </ul>
                ) : incidentNotes && !Array.isArray(incidentNotes) ? (
                  <ul className='flex flex-col gap-y-[10px]'>
                    <li className='font-poppins text-[#727272]' key={incidentNotes.NoteId}>{incidentNotes.Note}</li>
                  </ul>
                ) : (
                  <p className="font-poppins text-[#727272]">
                    There were no notes added for this incident.
                  </p>
                )}

              </div>
            </div>
            <div className='flex flex-col gap-y-[20px] pb-[75px]'>
              <div>
                <p className="font-poppins font-semibold text-[16px] mb-[10px]">Closure Notes</p>
                <p className='font-poppins text-[#727272]'>{incident?.ClosedOutcome}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    );
  }